import { useCallback, useEffect, useMemo, useState } from "react";
import Login from "./pages/login";
import Register from "./pages/RegisterPage";
import Dashboard from "./pages/dashboard";
import AdminMonitorPage from "./pages/AdminMonitorPage";
import LaunchSplash from "./components/LaunchSplash";
import {
  createTranslator,
  DEFAULT_LANGUAGE,
  getRuntimeLanguage,
  UI_LANGUAGE_STORAGE_KEY,
} from "./i18n";

const SPLASH_DURATION_MS = 2450;
const SPLASH_FADE_MS = 260;

export default function AppRoot() {
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [isRegister, setIsRegister] = useState(false);
  const [language, setLanguage] = useState(
    () => getRuntimeLanguage() || localStorage.getItem(UI_LANGUAGE_STORAGE_KEY) || DEFAULT_LANGUAGE
  );
  const [authContext, setAuthContext] = useState({
    prefillEmail: "",
    message: "",
  });
  const [showLaunchSplash, setShowLaunchSplash] = useState(() => process.env.NODE_ENV !== "test");
  const [isSplashLeaving, setIsSplashLeaving] = useState(false);
  const t = useMemo(() => createTranslator(language), [language]);
  const changeLanguage = useCallback((nextLanguage) => {
    localStorage.setItem(UI_LANGUAGE_STORAGE_KEY, nextLanguage);
    setLanguage(nextLanguage);
  }, []);

  useEffect(() => {
    localStorage.setItem(UI_LANGUAGE_STORAGE_KEY, language);
  }, [language]);

  useEffect(() => {
    document.body.classList.toggle("launch-splash-active", showLaunchSplash);
    return () => {
      document.body.classList.remove("launch-splash-active");
    };
  }, [showLaunchSplash]);

  useEffect(() => {
    if (!showLaunchSplash) {
      return undefined;
    }

    const leaveTimer = window.setTimeout(() => {
      setIsSplashLeaving(true);
    }, SPLASH_DURATION_MS);
    const hideTimer = window.setTimeout(() => {
      setShowLaunchSplash(false);
      setIsSplashLeaving(false);
    }, SPLASH_DURATION_MS + SPLASH_FADE_MS);

    return () => {
      window.clearTimeout(leaveTimer);
      window.clearTimeout(hideTimer);
    };
  }, [showLaunchSplash]);

  const appContent = token ? (
    <Dashboard
      token={token}
      language={language}
      setLanguage={changeLanguage}
      onLogout={() => setToken(null)}
    />
  ) : (
    <div className="auth-shell">
      <div className="auth-card surface-card">
          <div className="auth-brand">
            <div className="auth-brand-badge">PennyPantry</div>
          <h1>{t("auth.brand.title", "Shopping savings with maps and route intelligence")}</h1>
          <p>
            {t(
              "auth.brand.description",
              "Compare real nearby markets, build smart lists and follow updated catalogs in a mobile-first flow."
            )}
          </p>
        </div>

        {isRegister ? (
          <Register
            setToken={setToken}
            language={language}
            setLanguage={changeLanguage}
            onRegistered={({ email, message }) => {
              setAuthContext({
                prefillEmail: email || "",
                message: message || "",
              });
              setIsRegister(false);
            }}
          />
        ) : (
          <Login
            setToken={setToken}
            language={language}
            setLanguage={changeLanguage}
            initialEmail={authContext.prefillEmail}
            authMessage={authContext.message}
            clearAuthMessage={() =>
              setAuthContext((current) => ({
                ...current,
                message: "",
              }))
            }
          />
        )}

        <button
          className="auth-secondary"
          onClick={() => {
            setIsRegister(!isRegister);
            setAuthContext((current) => ({
              ...current,
              message: "",
            }));
          }}
        >
          {isRegister
            ? t("app.toggle.login", "I already have an account")
            : t("app.toggle.register", "Create account")}
        </button>
      </div>
    </div>
  );

  const isAdminMonitor = window.location?.pathname === "/admin-monitor";

  return (
    <>
      {isAdminMonitor ? <AdminMonitorPage /> : appContent}
      {!isAdminMonitor && showLaunchSplash ? <LaunchSplash leaving={isSplashLeaving} /> : null}
    </>
  );
}
